import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {IMqttServiceOptions, MqttService} from 'ngx-mqtt';
import {map} from 'rxjs/operators';
import {environment} from '@/environments/environment';
import {APIData} from '../model/common';

interface Connection {
    endpoint_url: string;
    topic: string;
    client_id: string;
    expires_seconds: number;
}

interface ConnectionRenew {
    expires_seconds: number;
}

interface Subscription {
    subscription: any;
    renewalTimeout: ReturnType<typeof setTimeout> | undefined;
    callbacks: Array<(msg: any) => void>;
}

@Injectable({
    providedIn: 'root'
})
export class MqttConnectionService {
    private readonly emsUrl = environment.emsUrl;
    private readonly subscriptions = new Map<string, Subscription>();
    private readonly topicToKey = new Map<string, string>();
    private readonly DEBUG = false;

    constructor(
        private http: HttpClient,
        private mqttService: MqttService
    ) {
    }

    subscribeToTopic(
        ids: string[],
        resource: string,
        parseFunction: (msg: any) => void
    ): void {
        const key = `${resource}:${ids.sort().join(',')}`;

        const existing = this.subscriptions.get(key);
        if (existing) {
            existing.callbacks.push(parseFunction);
            this.DEBUG && console.log(`Added callback to existing subscription for ${key}`);
            return;
        }

        this.DEBUG && console.log(`Creating new MQTT subscription for ${key}`);
        this.http.post<APIData<Connection>>(`${this.emsUrl}/v1/connection`, {ids, resource}, {withCredentials: true})
            .pipe(map(({data}) => data))
            .subscribe({
                next: (connection) => {
                    this.DEBUG && console.log(`Received connection for ${key}, topic: ${connection.topic}`);
                    this.connectAndSubscribe(key, connection, parseFunction);
                },
                error: (error) => {
                    console.error(`Error subscribing to changes for ${resource}`, error);
                }
            });
    }

    private connectAndSubscribe(
        key: string,
        connection: Connection,
        parseFunction: (msg: any) => void
    ): void {
        const match = /(.*?):\/\/(.*?)(\/.*)/.exec(connection.endpoint_url);
        if (!match) {
            console.error('Invalid endpoint URL');
            return;
        }

        const [, protocol, hostname, path] = match;

        // ngx-mqtt's connect() ends any existing client connection; for AWS IoT presigned URLs this
        // would drop prior topic subscriptions. So only connect if the current client is absent/unusable.
        const client: any = (this.mqttService as any).client;
        const hasUsableClient = !!client && !client.disconnecting;

        if (!hasUsableClient) {
            this.DEBUG && console.log(`Connecting to MQTT: ${protocol}://${hostname}${path}`, new Date().toISOString());
            this.mqttService.connect({
                url: connection.endpoint_url,
                protocol: (protocol as IMqttServiceOptions['protocol']),
                hostname,
                path,
                port: 443
            });
        }

        this.DEBUG && console.log(`Subscribing to MQTT topic: ${connection.topic} (key=${key})`, new Date().toISOString());

        const sub = this.mqttService.observe(connection.topic).subscribe({
            next: (resp: any) => {
                try {
                    const msg = JSON.parse(new TextDecoder('utf-8').decode(resp.payload));
                    this.DEBUG && console.log(`Received MQTT message on topic ${connection.topic}:`, new Date().toISOString(), msg);

                    const subscription = this.subscriptions.get(key);
                    subscription?.callbacks.forEach(callback => callback(msg));
                } catch (error) {
                    console.error('Error parsing MQTT message:', error);
                }
            },
            error: (error: any) => {
                console.error(`Error receiving MQTT message: ${JSON.stringify(error)}`);
            }
        });

        const renewalTimeout = setTimeout(
            () => this.renewSubscription(key, connection.client_id),
            connection.expires_seconds * 0.5 * 1000
        );

        this.subscriptions.set(key, {
            subscription: sub,
            renewalTimeout,
            callbacks: [parseFunction]
        });
        this.topicToKey.set(connection.topic, key);
    }

    private renewSubscription(
        key: string,
        client_id: string
    ): void {
        this.http.get<APIData<ConnectionRenew>>(`${this.emsUrl}/v1/connection/renew/${client_id}`, {withCredentials: true})
            .subscribe({
                next: (response) => {
                    const current = this.subscriptions.get(key);
                    if (!current) {
                        return;
                    }

                    if (current.renewalTimeout) {
                        clearTimeout(current.renewalTimeout);
                    }

                    const renewalTimeout = setTimeout(
                        () => this.renewSubscription(key, client_id),
                        response.data.expires_seconds * 0.5 * 1000
                    );

                    this.subscriptions.set(key, {
                        ...current,
                        renewalTimeout
                    });
                },
                error: (error) => {
                    console.error('Error renewing subscription for client ID ' + client_id, error);
                }
            });
    }

    unsubscribe(ids: string[], resource: string): void {
        const key = `${resource}:${ids.sort().join(',')}`;
        const sub = this.subscriptions.get(key);

        if (!sub) {
            return;
        }

        this.DEBUG && console.log(`Unsubscribing for ${key}`);
        sub.subscription.unsubscribe();
        if (sub.renewalTimeout) {
            clearTimeout(sub.renewalTimeout);
        }
        this.subscriptions.delete(key);

        for (const [topic, mappedKey] of this.topicToKey.entries()) {
            if (mappedKey === key) {
                this.topicToKey.delete(topic);
            }
        }
    }

    unsubscribeAll(): void {
        this.subscriptions.forEach((sub) => {
            sub.subscription.unsubscribe();
            if (sub.renewalTimeout) {
                clearTimeout(sub.renewalTimeout);
            }
        });
        this.subscriptions.clear();
        this.topicToKey.clear();
    }
}